import { DateTime } from "luxon";
import { useState } from "react";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { Flex } from "../../components/Flex";
import { H3 } from "../../components/Heading";
import { Paragraph } from "../../components/Paragraph";
import { CheckStatus } from "../../types/Individual";
import { AddressVerificationCheck } from "./AddressVerificationCheck";
import { GeoLocationCheck } from "./GeoLcationCheck";
import { IdentityLivenessCheck } from "./IdentityLivenessCheck";
import { PepAndSanctionsCheck } from "./PepAndSanctionsCheck";
import { BankDetailsCheck } from "./BankDetailsCheck";
import { PersonalDetailsCheck } from "./PersonalDetailsCheck";
import { EntityDetailsCheck } from "./EntityDetailsCheck";
import { AddressUpdateCheck } from "./AddressUpdateCheck";
import { PreviousNamesCheck } from "./PreviousNamesCheck";
import { AssociatedPartiesCheck } from "./AssociatedPartiesCheck";
import { NatureOfBusinessCheck } from "./NatureOfBusinessCheck";
import { ProofOfAddressCheck } from "./ProofOfAddressCheck";
import { Image } from "../../components/Image";
import {
  IndividualChecksMap,
  checksStatusIconsMap,
  checksStatusName,
} from "./helper";
import { queryClient } from "../../queryClient";
import { useParams } from "react-router-dom";
import { AdverseMediaCheck } from "./AdverseMediaCheck";
import { SourceOfFundsCheck } from "./SourceOfFundsCheck";
import { SanctionsCheck } from "./SanctionCheck";
import { ScreeningCheck } from "./ScreeningCheck";

type Props = {
  title: string;
  status: CheckStatus;
  id: number;
  name: string;
  date: string;
  useSlider: boolean;
};

export const CheckSection = (props: Props) => {
  const params = useParams();
  const [isIdAndLivenessCheck, setIsIdAndLivenessCheck] = useState(false);
  const [isPersonalDetailsCheck, setIsPersonalDetailsCheck] = useState(false);
  const [isEntityDetailsCheck, setIsEntityDetailsCheck] = useState(false);
  const [isAddressUpdateCheck, setIsAddressUpdateCheck] = useState(false);
  const [isPreviousNamesCheck, setIsPreviousNamesCheck] = useState(false);
  const [isAssociatedPartiesCheck, setIsAssociatedPartiesCheck] =
    useState(false);
  const [isNatureOfBusinessCheck, setIsNatureOfBusinessCheck] = useState(false);
  const [isPepAndSanctionsCheck, setIsPepAndSanctionsCheck] = useState(false);
  const [isBankDetailsCheck, setIsBankDetailsCheck] = useState(false);
  const [isProofOfAddressCheck, setIsProofOfAddressCheck] = useState(false);
  const [isAddressVerificationCheck, setIsAddressVerificationCheck] =
    useState(false);
  const [isGeoLocationCheck, setIsGeoLocationCheck] = useState(false);
  const [isAdverseMediaCheck, setIsAdverseMediaCheck] = useState(false);
  const [isSourceOfFundsCheck, setIsSourceOfFundsCheck] = useState(false);
  const [isSanctionsCheck, setIsSanctionsCheck] = useState(false);
  const [isScreeningCheck, setIsScreeningCheck] = useState(false);

  const [checkId, setCheckId] = useState(null);

  const handleViewClick = (name: string, id: number) => {
    setCheckId(id);

    if (name === IndividualChecksMap.address_verification_check.name) {
      setIsAddressVerificationCheck(!isAddressVerificationCheck);
    }
    if (name === IndividualChecksMap.proof_of_address_check.name) {
      setIsProofOfAddressCheck(!isProofOfAddressCheck);
    }
    if (name === IndividualChecksMap["pep_&_sanctions_check"].name) {
      setIsPepAndSanctionsCheck(!isPepAndSanctionsCheck);
    }
    if (name === IndividualChecksMap.bank_account_details_check.name) {
      setIsBankDetailsCheck(!isBankDetailsCheck);
    }
    if (name === IndividualChecksMap.personal_details_match_check.name) {
      setIsPersonalDetailsCheck(!isPersonalDetailsCheck);
    }
    if (name === IndividualChecksMap.entity_details_match_check.name) {
      setIsEntityDetailsCheck(!isEntityDetailsCheck);
    }
    if (name === IndividualChecksMap.address_update_check.name) {
      setIsAddressUpdateCheck(!isAddressUpdateCheck);
    }
    if (name === IndividualChecksMap.entity_previous_name_check.name) {
      setIsPreviousNamesCheck(!isPreviousNamesCheck);
    }
    if (name === IndividualChecksMap.parties_check.name) {
      setIsAssociatedPartiesCheck(!isAssociatedPartiesCheck);
    }
    if (name === IndividualChecksMap.nature_of_business_check.name) {
      setIsNatureOfBusinessCheck(!isNatureOfBusinessCheck);
    }
    if (name === IndividualChecksMap["id_&_liveliness_check"].name) {
      setIsIdAndLivenessCheck(!isIdAndLivenessCheck);
    }
    if (name === IndividualChecksMap.geo_location_check.name) {
      setIsGeoLocationCheck(!isGeoLocationCheck);
    }
    if (
      name === IndividualChecksMap.adverse_media_check.name ||
      name === IndividualChecksMap.entity_adverse_media_check.name
    ) {
      setIsAdverseMediaCheck(!isAdverseMediaCheck);
    }
    if (name === IndividualChecksMap.source_of_funds_check.name) {
      setIsSourceOfFundsCheck(!isSourceOfFundsCheck);
    }
    if (name === IndividualChecksMap.sanctions_check.name) {
      setIsSanctionsCheck(!isSanctionsCheck);
    }
    if (
      name === IndividualChecksMap.ln_screening_check.name ||
      name === IndividualChecksMap.ln_screening_check_entity.name
    ) {
      setIsScreeningCheck(!isScreeningCheck);
    }
  };

  function isDate(date) {
    return isNaN(date) && !isNaN(Date.parse(date));
  }

  const refetchIndividual = () => {
    queryClient.invalidateQueries(["individual", params.id]);
  };

  return (
    <>
      <Card mb={2} minHeight="97px">
        <Flex alignItems="center" mb={1}>
          <Image
            src={checksStatusIconsMap[props.status.status]}
            alt="status"
            height="21px"
            width="21px"
            mr={1}
          />
          <Paragraph fontSize={1} mt="0px" mb="0px">
            {props.name === IndividualChecksMap.proof_of_address_check.name &&
            props.status.status === "accepted"
              ? "Uploaded"
              : checksStatusName[props.status.status]}
          </Paragraph>
        </Flex>

        <H3>{props.title}</H3>

        <Flex justifyContent="space-between" alignItems="center">
          <Paragraph fontSize={1} mt={1} mb={0} color="gray.40">
            {isDate(props.date)
              ? DateTime.fromISO(props.date).toFormat("dd/MM/yyyy")
              : props.date}
          </Paragraph>

          {props.status.status !== "pending" && (
            <Button
              size="regular"
              variant="text"
              fontWeight="bold"
              onClick={() => handleViewClick(props.name, props.id)}
            >
              View
            </Button>
          )}
        </Flex>
      </Card>

      {isPersonalDetailsCheck && (
        <PersonalDetailsCheck
          isOpen={isPersonalDetailsCheck}
          onRequestClose={() => {
            setIsPersonalDetailsCheck(false);
            refetchIndividual();
          }}
          id={checkId}
        />
      )}

      {isEntityDetailsCheck && (
        <EntityDetailsCheck
          isOpen={isEntityDetailsCheck}
          onRequestClose={() => {
            setIsEntityDetailsCheck(false);
            refetchIndividual();
          }}
          id={checkId}
        />
      )}

      {isAddressUpdateCheck && (
        <AddressUpdateCheck
          isOpen={isAddressUpdateCheck}
          onRequestClose={() => {
            setIsAddressUpdateCheck(false);
            refetchIndividual();
          }}
          id={checkId}
        />
      )}

      {isPreviousNamesCheck && (
        <PreviousNamesCheck
          isOpen={isPreviousNamesCheck}
          onRequestClose={() => {
            setIsPreviousNamesCheck(false);
            refetchIndividual();
          }}
          id={checkId}
        />
      )}

      {isAssociatedPartiesCheck && (
        <AssociatedPartiesCheck
          isOpen={isAssociatedPartiesCheck}
          onRequestClose={() => {
            setIsAssociatedPartiesCheck(false);
            refetchIndividual();
          }}
          id={checkId}
        />
      )}

      {isNatureOfBusinessCheck && (
        <NatureOfBusinessCheck
          isOpen={isNatureOfBusinessCheck}
          onRequestClose={() => {
            setIsNatureOfBusinessCheck(false);
            refetchIndividual();
          }}
          id={checkId}
        />
      )}

      {isAddressVerificationCheck && (
        <AddressVerificationCheck
          isOpen={isAddressVerificationCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsAddressVerificationCheck(false);
          }}
          id={checkId}
        />
      )}

      {isProofOfAddressCheck && (
        <ProofOfAddressCheck
          isOpen={isProofOfAddressCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsProofOfAddressCheck(false);
          }}
          id={checkId}
        />
      )}

      {isPepAndSanctionsCheck && (
        <PepAndSanctionsCheck
          isOpen={isPepAndSanctionsCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsPepAndSanctionsCheck(false);
          }}
          id={checkId}
        />
      )}

      {isBankDetailsCheck && (
        <BankDetailsCheck
          isOpen={isBankDetailsCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsBankDetailsCheck(false);
          }}
          id={checkId}
        />
      )}

      {isIdAndLivenessCheck && (
        <IdentityLivenessCheck
          isOpen={isIdAndLivenessCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsIdAndLivenessCheck(false);
          }}
          id={checkId}
        />
      )}

      {isGeoLocationCheck && (
        <GeoLocationCheck
          isOpen={isGeoLocationCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsGeoLocationCheck(false);
          }}
          id={checkId}
        />
      )}

      {isAdverseMediaCheck && (
        <AdverseMediaCheck
          isOpen={isAdverseMediaCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsAdverseMediaCheck(false);
          }}
          id={checkId}
        />
      )}

      {isSourceOfFundsCheck && (
        <SourceOfFundsCheck
          isOpen={isSourceOfFundsCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsSourceOfFundsCheck(false);
          }}
          id={checkId}
        />
      )}

      {isSanctionsCheck && (
        <SanctionsCheck
          isOpen={isSanctionsCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsSanctionsCheck(false);
          }}
          id={checkId}
        />
      )}

      {isScreeningCheck && (
        <ScreeningCheck
          isOpen={isScreeningCheck}
          onRequestClose={() => {
            refetchIndividual();
            setIsScreeningCheck(false);
          }}
          id={checkId}
          type={
            props.name === IndividualChecksMap.ln_screening_check_entity.name
              ? "entity"
              : "individual"
          }
        />
      )}
    </>
  );
};
